/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback } from "react";
import { atom, useRecoilState } from "recoil";

import api from "../Api";

import { useAuthStore } from "../Auth";

import Delivery from "../../models/Delivery";

import { decrypt } from "../../utils/decrypt";

const deliveryAtom = atom({
  key: "delivery",
  default: null,
});

export const useDeliveriesStore = () => {
  const [delivery, setDelivery] = useRecoilState(deliveryAtom);
  const { user } = useAuthStore().state;

  /**
   * 직접등록건 | 배송불가지역 취소
   */
  const cancel = useCallback(
    async ({ bookId, data }) => {
      return await api.put(`/deliveries/${bookId}/cancel`, data);
    },
    [api.put],
  );

  /**
   * 배송 상세 조회
   */
  const fetchById = useCallback(
    async (bookId) => {
      const _delivery = await api.get(`/v2/deliveries/${bookId}`);

      let delivery = {
        ..._delivery,
        senderAddress: decrypt(_delivery?.senderAddress),
        senderAddressDetail: decrypt(_delivery?.senderAddressDetail),
        senderAddressRoad: decrypt(_delivery?.senderAddressRoad),
        receiverAddress: decrypt(_delivery?.receiverAddress),
        receiverAddressDetail: decrypt(_delivery?.receiverAddressDetail),
        receiverAddressRoad: decrypt(_delivery?.receiverAddressRoad),
        senderMobile: decrypt(_delivery?.senderMobile),
        senderMobile2: decrypt(_delivery?.senderMobile2),
        senderName: decrypt(_delivery?.senderName),
        receiverMobile: decrypt(_delivery?.receiverMobile),
        receiverMobile2: decrypt(_delivery?.receiverMobile2),
        receiverName: decrypt(_delivery?.receiverName),
        unrefinedAddress: decrypt(_delivery?.unrefinedAddress),
      };

      setDelivery(new Delivery({ ...delivery, newRider: user }));
    },
    [api.get, user],
  );

  /**
   * 주문번호가 포함된 배송 상태 조회
   */
  const fetchStatusWithOrderId = useCallback(
    async (bookId) => {
      const deliveries = await api.get(
        `/deliveries/${bookId}/status-with-order-id`,
      );
      const _delivery = Array.isArray(deliveries) ? deliveries[0] : deliveries;

      if (!_delivery) {
        throw new Error("조회된 배송이 없습니다.");
      }

      setDelivery(new Delivery(_delivery));
    },
    [api.get],
  );

  /**
   * 동일 위치의 배송 목록 조회
   */
  const fetchSameReceiverDeliveries = useCallback(
    async (bookId) => {
      const { deliveries } = await api.get(
        `/deliveries/${bookId}/same-receiver-deliveries`,
      );

      const sameReceiverDeliveries = deliveries.filter(
        (d) => d.bookId !== bookId,
      );

      return sameReceiverDeliveries;
    },
    [api.get],
  );

  /**
   * 수거지의 동일 수거 배송 목록 조회
   */
  const fetchSameSpotPickups = useCallback(
    async (bookId) => {
      return await api.get(`/deliveries/${bookId}/same-spot-pickups`);
    },
    [api.get],
  );

  /**
   * 배송 경로(순서) 조회
   */
  const fetchRouteByBookId = useCallback(
    async (bookId) => {
      return await api.get(`/delivery-routes/by-book-id/${bookId}`);
    },
    [api.get],
  );

  /**
   * 재배차
   */
  const reallocate = useCallback(
    async ({ bookId, data }) => {
      return await api.put(`/deliveries/${bookId}/reallocation`, data);
    },
    [api.put],
  );

  /**
   * 번역 오류 보고
   */
  const reportTranslationError = useCallback(
    async ({ data }) => {
      return await api.post(`/address/translations/report`, data);
    },
    [api.post],
  );

  /**
   * 배송 지연
   */
  const setDelay = useCallback(
    async ({
      bookId,
      reason,
      reasonType,
      processType = "delivery",
      skipStatusChange = false,
      latitude,
      longitude,
    }) => {
      const body = {
        reason,
        reasonType,
        processType,
        skipStatusChange,
      };

      if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
        body.latitude = latitude;
        body.longitude = longitude;
      }

      return await api.put(`/deliveries/${bookId}/delay`, body);
    },
    [api.put],
  );

  /**
   * 배송 완료
   */
  const setDelivered = useCallback(
    async ({ bookId, data }) => {
      return await api.put(`/deliveries/${bookId}/delivery-completed`, data);
    },
    [api.put],
  );

  /**
   * 배송 완료 위치 저장
   */
  const setDeliveryLocation = useCallback(
    async ({ bookId, data }) => {
      return await api.put(
        `/deliveries/${bookId}/delivery-completed-location`,
        data,
      );
    },
    [api.put],
  );

  /**
   * 분실 완료
   */
  const setLost = useCallback(
    async ({ bookId, data }) => {
      return await api.put(`/deliveries/${bookId}/lost`, data);
    },
    [api.put],
  );

  /**
   * 수거 완료
   */
  const setPickup = useCallback(
    async ({ bookId, data }) => {
      return await api.put(`/deliveries/${bookId}/pickup-completed`, data);
    },
    [api.put],
  );

  /**
   * 배송 연기
   */
  const setPostponed = useCallback(
    async ({ bookId }) => {
      return await api.put(`/deliveries/${bookId}/postponed`);
    },
    [api.put],
  );

  /**
   * 이전 상태로
   */
  const setPrevStatusBulk = useCallback(
    async ({ bookIds, status }) => {
      return await api.put(`/deliveries/prev`, { bookIds, status });
    },
    [api.put],
  );

  /**
   * 반송 완료
   */
  const setSentBack = useCallback(
    async ({ bookId, data }) => {
      return await api.put(`/deliveries/${bookId}/sent-back`, data);
    },
    [api.put],
  );

  /**
   * 배송 출발
   */
  const setStart = useCallback(
    async ({ bookId }) => {
      return await api.put(`/v2/deliveries/${bookId}/delivery-start`);
    },
    [api.put],
  );

  /**
   * 주소 수정
   */
  const updateAddress = useCallback(
    async ({ bookId, data }) => {
      return await api.put(`/deliveries/${bookId}/address`, data);
    },
    [api.put],
  );

  /**
   * 주소 수정(번역 텍스트)
   */
  const updateAddressFromEtc4 = useCallback(
    async ({ bookId, data }) => {
      return await api.put(`/deliveries/${bookId}/address/from-etc4`, data);
    },
    [api.put],
  );

  /**
   * 배송 완료 이미지 재업로드
   */
  const updateImages = useCallback(
    async ({ bookId, data }) => {
      return await api.put(`/deliveries/${bookId}/images`, data);
    },
    [api.put],
  );

  const state = {
    delivery,
  };

  return {
    state,
    cancel,
    fetchById,
    fetchRouteByBookId,
    fetchStatusWithOrderId,
    fetchSameReceiverDeliveries,
    fetchSameSpotPickups,
    reallocate,
    reportTranslationError,
    setDelay,
    setDelivered,
    setDeliveryLocation,
    setLost,
    setPickup,
    setPostponed,
    setPrevStatusBulk,
    setSentBack,
    setStart,
    updateAddress,
    updateAddressFromEtc4,
    updateImages,
  };
};
